import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleDot,
  Clock,
  FileUp,
  Paperclip,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { Loading } from "@/components/common";
import { Action, Field, PageHeader, Panel, PanelHeader, StatusBadge } from "./ui";
import { ageLabel, wardLabel } from "@/lib/patient";

const onlyDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return d ? `${d}/${m}/${y}` : value;
};

const input =
  "h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15";
const area = `${input} h-auto py-2.5`;

const Info = ({ label, value, testId }) => (
  <div className="rounded-xl bg-sand/50 px-3.5 py-3">
    <p className="er-label text-[11px] tracking-[0.14em]">{label}</p>
    <p data-testid={testId} className="mt-1 text-[15.5px] font-semibold text-ink-800">
      {value}
    </p>
  </div>
);

const Critical = ({ label, testId }) => (
  <div
    data-testid={testId}
    className="flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 px-3.5 py-3 text-[14px] font-semibold text-red-800"
  >
    <AlertTriangle size={17} className="mt-0.5 shrink-0" aria-hidden="true" />
    <span>{label}</span>
  </div>
);

/** Formulário genérico: campos declarados, sem duplicar lógica por estado. */
const RecordForm = ({ title, fields, submitLabel, onSubmit, testId, children }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  return (
    <form
      data-testid={testId}
      className="space-y-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        const ok = await onSubmit(values);
        setSaving(false);
        if (ok) setValues({});
      }}
    >
      {title ? <p className="text-[14.5px] font-semibold text-ink-800">{title}</p> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
            <Field label={f.label} hint={f.hint}>
              {f.type === "textarea" ? (
                <textarea
                  rows={3}
                  data-testid={`${testId}-${f.key}`}
                  className={area}
                  value={values[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                />
              ) : f.type === "select" ? (
                <select
                  data-testid={`${testId}-${f.key}`}
                  className={input}
                  value={values[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  required={f.required}
                >
                  <option value="">Selecione…</option>
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type || "text"}
                  data-testid={`${testId}-${f.key}`}
                  className={input}
                  value={values[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  required={f.required}
                />
              )}
            </Field>
          </div>
        ))}
      </div>
      {children}
      <Action type="submit" icon={Plus} disabled={saving} data-testid={`${testId}-submit`}>
        {saving ? "Salvando…" : submitLabel}
      </Action>
    </form>
  );
};

export default function TimelineTrack() {
  const { patientId, track } = useParams();
  const { api } = useAuth();
  const [detail, setDetail] = useState(null);
  const [staff, setStaff] = useState([]);
  const [note, setNote] = useState("");
  const [justification, setJustification] = useState("");
  const [guardian, setGuardian] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portal/timeline/patients/${patientId}/tracks/${track}`);
      setDetail(data);
      setGuardian(data.patient?.guardian_name || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, track]);

  useEffect(() => {
    load();
    api
      .get("/portal/timeline/staff-options")
      .then(({ data }) => setStaff(data))
      .catch(() => setStaff([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const run = async (fn, success) => {
    try {
      const { data } = await fn();
      if (data?.summary) setDetail(data);
      else await load();
      toast.success(success);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  if (!detail) return <Loading label="Carregando tema…" />;

  const base = `/portal/timeline/patients/${patientId}/tracks/${track}`;
  const { summary, checklist, records, history, data, advance_options: options } = detail;
  const state = summary.state_code;
  const waiting = summary.deadlines?.inss_waiting;
  const attempts = records.filter((r) => r.kind === "contact_attempt");
  const pendencies = records.filter((r) => r.kind === "pendency");
  const familyContacts = records.filter((r) => r.kind === "family_contact");
  const attachments = records.filter((r) => r.kind === "attachment");
  const can = summary.editable || {};
  const stateDeadline = summary.deadlines?.state;
  const doneChecklist = checklist.filter((c) => c.done);

  const uploadFile = async (file, kind) => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    try {
      await api.post(`${base}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Anexo enviado.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const Uploader = ({ kind, label, testId }) => (
    <label
      data-testid={testId}
      className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2.5 text-[14px] font-semibold text-ink-700 hover:bg-sand"
    >
      <FileUp size={16} aria-hidden="true" /> {label}
      <input
        type="file"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], kind)}
      />
    </label>
  );

  return (
    <div data-testid="timeline-track-page" className="space-y-7">
      <PageHeader
        kicker={`${detail.patient.full_name} · ${ageLabel(detail.patient)}`}
        title={summary.track_label}
        subtitle={`Ala: ${wardLabel(detail.patient)} · Internação: ${onlyDate(detail.patient.admission_date)} · Etapa ${
          summary.progress.step
        } de ${summary.progress.total}${summary.outcome ? ` · ${summary.outcome}` : ""}`}
        actions={
          <Link to="/portal/linha-do-tempo">
            <Action variant="secondary" icon={ArrowLeft} data-testid="timeline-back">
              Voltar
            </Action>
          </Link>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[1.35fr,1fr]">
        <div className="space-y-6">
          {/* Estado atual e avanço */}
          <Panel testId="timeline-current-state" className="space-y-5">
            <PanelHeader
              label="Estado atual"
              title={`${state}. ${summary.state_label}`}
              hint={summary.active ? "Tema em andamento." : `Tema concluído em ${formatDate(summary.closed_at)}.`}
            />
            {summary.critical ? <Critical label={summary.critical_label} testId="timeline-critical-banner" /> : null}

            {summary.active ? (
              <div className="space-y-4">
                {options.some((o) => o.accepts_justification) ? (
                  <Field label="Justificativa do encerramento (opcional)">
                    <textarea
                      rows={2}
                      data-testid="timeline-justification"
                      className={area}
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                    />
                  </Field>
                ) : null}
                <div className="space-y-3">
                  {options.map((o) => (
                    <div key={o.to_state} className="rounded-xl border border-border p-3.5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-[14.5px] font-semibold text-ink-800">
                          {o.to_state}. {o.label}
                          {o.closes ? " (encerra o tema)" : ""}
                        </p>
                        <Action
                          icon={ArrowRight}
                          size="sm"
                          data-testid={`timeline-advance-${o.to_state}`}
                          disabled={o.blockers.length > 0}
                          onClick={() =>
                            run(
                              () =>
                                api.post(`${base}/advance`, {
                                  to_state: o.to_state,
                                  note: note || null,
                                  justification: o.accepts_justification ? justification : null,
                                }),
                              "Estado atualizado."
                            ).then((ok) => ok && setNote(""))
                          }
                        >
                          Avançar
                        </Action>
                      </div>
                      {o.blockers.length > 0 ? (
                        <ul
                          data-testid={`timeline-blockers-${o.to_state}`}
                          className="mt-2.5 space-y-1.5 text-[13.5px] text-amber-900"
                        >
                          {o.blockers.map((b) => (
                            <li key={b} className="flex gap-2">
                              <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                  {options.length === 0 ? (
                    <p className="text-[14px] text-ink-500">Não há próximos estados configurados para este ponto.</p>
                  ) : null}
                </div>

                <Field label="Observação registrada no histórico (opcional no avanço)">
                  <textarea
                    rows={2}
                    data-testid="timeline-note"
                    className={area}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>
                <Action
                  variant="secondary"
                  data-testid="timeline-note-save"
                  onClick={() =>
                    run(() => api.post(`${base}/notes`, { note }), "Observação registrada.").then(
                      (ok) => ok && setNote("")
                    )
                  }
                >
                  Registrar observação
                </Action>
              </div>
            ) : (
              <StatusBadge tone="success" testId="timeline-closed-badge">
                {summary.outcome} · {formatDate(summary.closed_at)}
              </StatusBadge>
            )}
          </Panel>

          {/* Prazo do estado atual */}
          {stateDeadline ? (
            <Panel testId="timeline-state-deadline-panel" className="space-y-4">
              <PanelHeader
                label="Prazo do estado"
                title={`Internação + ${stateDeadline.days} dia(s) corridos`}
                hint="Dias corridos, no fuso America/Sao_Paulo."
              />
              {stateDeadline.admission_pending ? (
                <div className="space-y-3">
                  <Critical label="Data de internação pendente" testId="timeline-deadline-admission-pending" />
                  <Link to={`/portal/pacientes/${patientId}`}>
                    <Action variant="secondary" data-testid="timeline-deadline-fix-admission">
                      Preencher a data no paciente
                    </Action>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Info label="Data de internação" value={onlyDate(stateDeadline.admission_date)} />
                    <Info label="Data-limite" value={onlyDate(stateDeadline.deadline)} testId="timeline-state-deadline" />
                    <Info
                      label={stateDeadline.late ? "Dias em atraso" : "Dias restantes"}
                      value={stateDeadline.late ? stateDeadline.days_late : stateDeadline.days_remaining}
                      testId="timeline-state-deadline-days"
                    />
                    <Info
                      label="Situação"
                      value={
                        stateDeadline.status === "late"
                          ? `Atrasado há ${stateDeadline.days_late} dia(s)`
                          : stateDeadline.status === "due_today"
                          ? "Vence hoje"
                          : "Dentro do prazo"
                      }
                      testId="timeline-state-deadline-status"
                    />
                  </div>
                  {stateDeadline.late ? (
                    <Critical
                      label={
                        track === "support_journey" && state === 5
                          ? "Crítico — primeiro contato atrasado"
                          : `Atrasado há ${stateDeadline.days_late} dia(s)`
                      }
                      testId="timeline-deadline-critical"
                    />
                  ) : (
                    <p className="flex items-center gap-2 text-[14px] font-semibold text-emerald-800">
                      <Clock size={16} aria-hidden="true" /> {stateDeadline.label}
                    </p>
                  )}
                </>
              )}
            </Panel>
          ) : null}

          {/* Requisitos do estado */}
          {track === "support_journey" && can.guardian ? (
            <Panel testId="timeline-guardian-panel" className="space-y-4">
              <PanelHeader
                label="Identificação de responsável"
                title="Nome do responsável pelo paciente"
                hint="É o mesmo dado do cadastro do paciente: salvar aqui atualiza o cadastro."
              />
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[240px] flex-1">
                  <Field label="Nome do responsável">
                    <input
                      data-testid="timeline-guardian-input"
                      className={input}
                      value={guardian}
                      onChange={(e) => setGuardian(e.target.value)}
                      placeholder="Nome de quem responde pelo paciente"
                    />
                  </Field>
                </div>
                <Action
                  data-testid="timeline-guardian-save"
                  disabled={!guardian.trim()}
                  onClick={() =>
                    run(() => api.put(`${base}/guardian`, { guardian_name: guardian }), "Responsável atualizado.")
                  }
                >
                  Salvar responsável
                </Action>
              </div>
            </Panel>
          ) : null}

          {track === "support_journey" && state === 2 ? (
            <Panel testId="timeline-support-grants" className="space-y-4">
              <PanelHeader
                label="Rede de Apoio autorizada"
                title={`${detail.context.active_grants} pessoa(s) autorizada(s)`}
                hint="Use o cadastro já existente do paciente — não há segundo cadastro de Rede de Apoio."
              />
              <Link to={`/portal/pacientes/${patientId}`}>
                <Action variant="secondary" data-testid="timeline-manage-grants">
                  Adicionar / gerenciar integrantes
                </Action>
              </Link>
            </Panel>
          ) : null}

          {(track === "support_journey" || track === "injunction") && can.checklist ? (
            <Panel testId="timeline-checklist-panel" className="space-y-4">
              <PanelHeader
                label="Checklist obrigatório"
                title={track === "injunction" ? "Necessidade confirmada" : "Anamnese recebida / Orientações"}
              />
              <ul className="space-y-2.5">
                {checklist.map((item) => (
                  <li
                    key={item.key}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-sand/50 px-3.5 py-3"
                  >
                    <label className="flex flex-1 items-start gap-3 text-[14px] text-ink-800">
                      <input
                        type="checkbox"
                        data-testid={`timeline-checklist-${item.key}`}
                        checked={!!item.done}
                        onChange={(e) =>
                          run(
                            () => api.post(`${base}/checklist`, { item_key: item.key, done: e.target.checked }),
                            "Checklist atualizado."
                          )
                        }
                        className="mt-1 h-4 w-4 accent-[#4F7A62]"
                      />
                      <span>
                        {item.label}
                        {item.done ? (
                          <span className="mt-0.5 block text-[12.5px] text-ink-500">
                            Concluído em {formatDate(item.done_at)} por {item.done_by_name || "—"}
                          </span>
                        ) : null}
                      </span>
                    </label>
                    {item.done ? <CheckCircle2 size={17} className="text-emerald-600" aria-hidden="true" /> : null}
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {track === "support_journey" && !can.checklist && doneChecklist.length > 0 ? (
            <Panel testId="timeline-checklist-readonly" className="space-y-3">
              <PanelHeader
                label="Checklist (somente consulta)"
                title="Itens concluídos"
                hint="Preservado para auditoria; deixa de ser formulário ativo nos estados seguintes."
              />
              <ul className="space-y-2">
                {doneChecklist.map((item) => (
                  <li key={item.key} className="flex items-start gap-2 text-[13.5px] text-ink-700">
                    <CheckCircle2 size={15} className="mt-0.5 text-emerald-600" aria-hidden="true" />
                    <span>
                      {item.label} · {formatDate(item.done_at)} · {item.done_by_name || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {track === "injunction" && !can.checklist && doneChecklist.length > 0 ? (
            <Panel testId="timeline-checklist-readonly-injunction" className="space-y-3">
              <PanelHeader label="Checklist (somente consulta)" title="Necessidade confirmada" />
              <ul className="space-y-2">
                {doneChecklist.map((item) => (
                  <li key={item.key} className="flex items-start gap-2 text-[13.5px] text-ink-700">
                    <CheckCircle2 size={15} className="mt-0.5 text-emerald-600" aria-hidden="true" />
                    <span>
                      {item.label} · {formatDate(item.done_at)} · {item.done_by_name || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {track === "support_journey" && can.contact_attempt ? (
            <Panel testId="timeline-first-contact-panel" className="space-y-5">
              <PanelHeader label="Primeiro contato do conselheiro" title="Tentativas de contato" />
              <div className="grid gap-3 sm:grid-cols-2">
                <Info label="Tentativas registradas" value={attempts.length} testId="timeline-attempts-count" />
                <Info
                  label="Tentativa mais recente"
                  value={
                    attempts.length
                      ? `${attempts[attempts.length - 1].outcome === "success" ? "Bem-sucedida" : "Mal-sucedida"} · ${formatDate(
                          attempts[attempts.length - 1].happened_at
                        )}`
                      : "Nenhuma tentativa"
                  }
                  testId="timeline-last-attempt"
                />
              </div>

              <RecordForm
                testId="timeline-attempt-form"
                title="Registrar tentativa de contato"
                submitLabel="Registrar tentativa"
                fields={[
                  { key: "happened_at", label: "Data e hora", type: "datetime-local", required: true },
                  {
                    key: "outcome",
                    label: "Resultado",
                    type: "select",
                    required: true,
                    options: [
                      { value: "success", label: "Bem-sucedida" },
                      { value: "failure", label: "Mal-sucedida" },
                    ],
                  },
                  { key: "notes", label: "Observações", type: "textarea", wide: true },
                ]}
                onSubmit={(v) =>
                  run(
                    () =>
                      api.post(`${base}/contact-attempts`, {
                        happened_at: v.happened_at,
                        outcome: v.outcome,
                        notes: v.notes || null,
                      }),
                    "Tentativa registrada."
                  )
                }
              />

              <ul data-testid="timeline-attempts-list" className="space-y-2.5">
                {attempts.map((a) => (
                  <li key={a.id} className="flex items-start gap-3 rounded-xl bg-sand/50 px-3.5 py-3">
                    <CircleDot
                      size={16}
                      className={`mt-0.5 ${a.outcome === "success" ? "text-emerald-600" : "text-red-600"}`}
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-[14px] font-semibold text-ink-800">
                        {a.outcome === "success" ? "Bem-sucedida" : "Mal-sucedida"} · {formatDate(a.happened_at)}
                      </p>
                      {a.notes ? <p className="text-[13.5px] text-ink-600">{a.notes}</p> : null}
                      <p className="text-[12.5px] text-ink-500">
                        Registrado por {a.created_by_name || "—"} em {formatDate(a.created_at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {/* INSS */}
          {track === "inss" ? (
            <Panel testId="timeline-inss-panel" className="space-y-5">
              <PanelHeader
                label="Afastamento (INSS)"
                title="Responsável, DUT e prazo de 14 dias"
                hint="DUT é o documento que informa a data do último dia trabalhado."
              />
              {can.assignee ? (
                <Field label="Responsável (base de funcionários)">
                  <select
                    data-testid="timeline-assignee-select"
                    className={input}
                    value={summary.assignee_id || ""}
                    onChange={(e) =>
                      run(() => api.put(`${base}/assignee`, { staff_id: e.target.value || null }), "Responsável definido.")
                    }
                  >
                    <option value="">Não definido</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Info
                  label="Responsável (somente consulta)"
                  value={summary.assignee_name || "Não definido"}
                  testId="timeline-assignee-readonly"
                />
              )}

              {waiting ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Info label="Último dia trabalhado" value={onlyDate(waiting.last_work_date)} testId="timeline-dut-date" />
                  <Info label="Dias transcorridos" value={waiting.days_elapsed} testId="timeline-dut-elapsed" />
                  <Info label="Dias restantes" value={waiting.days_remaining} testId="timeline-dut-remaining" />
                  <Info label="Liberação para entrada" value={onlyDate(waiting.release_date)} testId="timeline-dut-release" />
                  <div className="sm:col-span-2">
                    <p
                      data-testid="timeline-dut-status"
                      className={`text-[14px] font-semibold ${waiting.released ? "text-emerald-800" : "text-ink-700"}`}
                    >
                      {waiting.label}
                    </p>
                  </div>
                </div>
              ) : null}

              {can.dut ? (
                <RecordForm
                  testId="timeline-dut-form"
                  title="Registrar DUT recebida"
                  submitLabel="Salvar DUT"
                  fields={[
                    { key: "last_work_date", label: "Data do último dia trabalhado", type: "date", required: true },
                    { key: "received_at", label: "Data de recebimento", type: "date" },
                    { key: "notes", label: "Observações", type: "textarea", wide: true },
                  ]}
                  onSubmit={(v) =>
                    run(
                      () =>
                        api.post(`${base}/dut`, {
                          last_work_date: v.last_work_date,
                          received_at: v.received_at || null,
                          notes: v.notes || null,
                        }),
                      "DUT registrada."
                    )
                  }
                >
                  <Uploader kind="dut" label="Anexar documento da DUT (opcional)" testId="timeline-dut-upload" />
                </RecordForm>
              ) : null}

              {can.pendency ? (
                <div className="space-y-3">
                  <p className="text-[14.5px] font-semibold text-ink-800">Pendências</p>
                  <RecordForm
                    testId="timeline-pendency-form"
                    submitLabel="Cadastrar pendência"
                    fields={[
                      { key: "description", label: "Descrição", required: true, wide: true },
                      {
                        key: "assignee_id",
                        label: "Responsável",
                        type: "select",
                        options: staff.map((s) => ({ value: s.id, label: s.name })),
                      },
                      { key: "notes", label: "Observações", type: "textarea" },
                    ]}
                    onSubmit={(v) =>
                      run(
                        () =>
                          api.post(`${base}/pendencies`, {
                            description: v.description,
                            assignee_id: v.assignee_id || null,
                            notes: v.notes || null,
                          }),
                        "Pendência cadastrada."
                      )
                    }
                  />
                </div>
              ) : null}

              {pendencies.length > 0 ? (
                <ul data-testid="timeline-pendencies-list" className="space-y-2.5">
                  {pendencies.map((p) => (
                    <li
                      key={p.id}
                      className="flex flex-wrap items-start justify-between gap-3 rounded-xl bg-sand/50 px-3.5 py-3"
                    >
                      <div>
                        <p className="text-[14px] font-semibold text-ink-800">{p.description}</p>
                        <p className="text-[12.5px] text-ink-500">
                          {p.status === "resolved"
                            ? `Resolvida em ${formatDate(p.resolved_at)} por ${p.resolved_by_name || "—"}`
                            : `Aberta em ${formatDate(p.created_at)}${p.assignee_name ? ` · ${p.assignee_name}` : ""}`}
                        </p>
                      </div>
                      {p.status === "pending" ? (
                        <Action
                          size="sm"
                          variant="secondary"
                          data-testid={`timeline-resolve-pendency-${p.id}`}
                          onClick={() =>
                            run(
                              () => api.post(`/portal/timeline/pendencies/${p.id}/resolve`, { notes: null }),
                              "Pendência resolvida."
                            )
                          }
                        >
                          Resolver
                        </Action>
                      ) : (
                        <StatusBadge tone="success">Resolvida</StatusBadge>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}

              {can.decision ? (
                <RecordForm
                  testId="timeline-decision-form"
                  title="Decisão do INSS (obrigatória para encerrar em 97/98)"
                  submitLabel="Registrar decisão"
                  fields={[
                    { key: "decided_at", label: "Data da decisão", type: "date", required: true },
                    { key: "notes", label: "Observações", type: "textarea" },
                  ]}
                  onSubmit={(v) =>
                    run(
                      () => api.post(`${base}/decision`, { decided_at: v.decided_at, notes: v.notes || null }),
                      "Decisão registrada."
                    )
                  }
                >
                  <Uploader kind="decision" label="Anexar documento da decisão" testId="timeline-inss-decision-upload" />
                </RecordForm>
              ) : data.decision?.decided_at ? (
                <Info
                  label="Decisão do INSS (somente consulta)"
                  value={`${onlyDate(data.decision.decided_at)}${data.decision.notes ? ` · ${data.decision.notes}` : ""}`}
                  testId="timeline-decision-readonly"
                />
              ) : null}
            </Panel>
          ) : null}

          {/* Liminar */}
          {track === "injunction" ? (
            <Panel testId="timeline-injunction-panel" className="space-y-5">
              <PanelHeader label="Liminar" title="Família, documentos, entrevista, relatório e decisão" />

              {can.family_contact ? (
                <RecordForm
                  testId="timeline-family-form"
                  title="Comunicação com a família ou responsável"
                  submitLabel="Registrar comunicação"
                  fields={[
                    { key: "happened_at", label: "Data e hora", type: "datetime-local", required: true },
                    { key: "person", label: "Pessoa contatada", required: true },
                    { key: "notes", label: "Observações", type: "textarea", wide: true },
                  ]}
                  onSubmit={(v) =>
                    run(
                      () =>
                        api.post(`${base}/family-contacts`, {
                          happened_at: v.happened_at,
                          person: v.person,
                          notes: v.notes || null,
                        }),
                      "Comunicação registrada."
                    )
                  }
                />
              ) : null}
              <ul data-testid="timeline-family-list" className="space-y-2">
                {familyContacts.map((c) => (
                  <li key={c.id} className="rounded-xl bg-sand/50 px-3.5 py-2.5 text-[13.5px] text-ink-700">
                    {c.person} · {formatDate(c.happened_at)} · registrado por {c.created_by_name || "—"}
                  </li>
                ))}
              </ul>

              <div className="space-y-3">
                <p className="text-[14.5px] font-semibold text-ink-800">Documentos solicitados e recebidos</p>
                <Uploader kind="document" label="Anexar documento" testId="timeline-doc-upload" />
                <Uploader kind="report" label="Anexar/atualizar relatório" testId="timeline-report-upload" />
                <ul data-testid="timeline-attachments-list" className="space-y-2">
                  {attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 text-[13.5px] text-ink-700">
                      <Paperclip size={14} aria-hidden="true" />
                      {a.filename} · {a.document_kind} · {formatDate(a.created_at)}
                    </li>
                  ))}
                </ul>
              </div>

              {data.interview?.scheduled_date ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Info label="Data da entrevista" value={onlyDate(data.interview.scheduled_date)} testId="timeline-interview-date" />
                  <Info label="Horário" value={data.interview.scheduled_time} testId="timeline-interview-time" />
                  <div className="sm:col-span-2">
                    <Info label="Participantes" value={data.interview.participants} testId="timeline-interview-participants" />
                  </div>
                  {data.interview.held_at ? (
                    <div className="sm:col-span-2">
                      <Info label="Realizada em" value={formatDate(data.interview.held_at)} />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {can.interview_schedule ? (
                <RecordForm
                  testId="timeline-interview-form"
                  title="Agendar entrevista"
                  submitLabel="Salvar agendamento"
                  fields={[
                    { key: "scheduled_date", label: "Data da entrevista", type: "date", required: true },
                    { key: "scheduled_time", label: "Horário", type: "time", required: true },
                    { key: "participants", label: "Participantes", required: true, wide: true },
                    { key: "notes", label: "Observações", type: "textarea", wide: true },
                  ]}
                  onSubmit={(v) =>
                    run(
                      () =>
                        api.post(`${base}/interview`, {
                          scheduled_date: v.scheduled_date,
                          scheduled_time: v.scheduled_time,
                          participants: v.participants,
                          notes: v.notes || null,
                        }),
                      "Entrevista agendada."
                    )
                  }
                />
              ) : null}

              {can.interview_held && !data.interview?.held_at ? (
                <RecordForm
                  testId="timeline-interview-held-form"
                  title="Registrar realização da entrevista"
                  submitLabel="Registrar realização"
                  fields={[
                    { key: "held_at", label: "Data e hora da realização", type: "datetime-local", required: true },
                    { key: "notes", label: "Observações", type: "textarea" },
                  ]}
                  onSubmit={(v) =>
                    run(
                      () => api.post(`${base}/interview/held`, { held_at: v.held_at, notes: v.notes || null }),
                      "Realização registrada."
                    )
                  }
                />
              ) : null}

              {can.report_dispatch ? (
                <RecordForm
                  testId="timeline-dispatch-form"
                  title="Encaminhamento do relatório"
                  submitLabel="Registrar encaminhamento"
                  fields={[
                    { key: "sent_at", label: "Data do encaminhamento", type: "date", required: true },
                    { key: "recipient", label: "Destinatário", required: true },
                    { key: "method", label: "Forma de encaminhamento", required: true },
                    { key: "notes", label: "Observações", type: "textarea" },
                  ]}
                  onSubmit={(v) =>
                    run(
                      () =>
                        api.post(`${base}/report-dispatch`, {
                          sent_at: v.sent_at,
                          recipient: v.recipient,
                          method: v.method,
                          notes: v.notes || null,
                        }),
                      "Encaminhamento registrado."
                    )
                  }
                />
              ) : null}

              {can.decision ? (
                <RecordForm
                  testId="timeline-injunction-decision-form"
                  title="Decisão judicial (obrigatória para encerrar em 97/98)"
                  submitLabel="Registrar decisão"
                  fields={[
                    { key: "decided_at", label: "Data da decisão", type: "date", required: true },
                    { key: "notes", label: "Observações", type: "textarea" },
                  ]}
                  onSubmit={(v) =>
                    run(
                      () => api.post(`${base}/decision`, { decided_at: v.decided_at, notes: v.notes || null }),
                      "Decisão registrada."
                    )
                  }
                >
                  <Uploader kind="decision" label="Anexar documento da decisão" testId="timeline-decision-upload" />
                </RecordForm>
              ) : null}
            </Panel>
          ) : null}
        </div>

        {/* Histórico do tema */}
        <Panel testId="timeline-track-history" className="space-y-4">
          <PanelHeader
            label="Histórico do tema"
            title="Ordem cronológica"
            hint="Preservado inclusive após a conclusão."
          />
          {history.length === 0 ? (
            <p className="text-[14px] text-ink-500">Nenhum registro ainda.</p>
          ) : (
            <ol className="space-y-3">
              {history.map((e) => (
                <li key={e.id} className="rounded-xl bg-sand/50 px-3.5 py-3">
                  <p className="text-[14px] font-semibold text-ink-800">
                    {e.type === "transition" ? `${e.from_label} → ${e.to_label}` : e.note || e.type}
                  </p>
                  {e.type === "transition" && e.note ? (
                    <p className="mt-0.5 text-[13.5px] text-ink-600">{e.note}</p>
                  ) : null}
                  {e.justification ? (
                    <p className="mt-0.5 text-[13.5px] text-ink-600">Justificativa: {e.justification}</p>
                  ) : null}
                  <p className="mt-1 text-[12.5px] text-ink-500">
                    {formatDate(e.created_at)} · {e.actor_name || "—"}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>
    </div>
  );
}
